import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { backfillMaintenanceDebtPaymentToTreasury } from '../services/debtService';
import { resetWarehouses, getAllWarehouses, deleteAllWarehouses, createDefaultWarehouse, deleteWarehouse } from '../services/inventoryService';
import { Warehouse } from '../utils/types';
import { useNavigate } from 'react-router-dom';

const AdminToolsPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [backfillParams, setBackfillParams] = useState({
    orderNumber: '',
    customerName: '',
    dateISO: ''
  });
  const [backfilling, setBackfilling] = useState(false);


  const navigate = useNavigate();

  // تحميل المخازن عند تحميل الصفحة
  useEffect(() => {
    loadWarehouses();
  }, []);

  // تحميل المخازن
  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const warehouseData = await getAllWarehouses();
      setWarehouses(warehouseData);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحميل المخازن',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // حذف جميع المخازن
  const handleDeleteAllWarehouses = async () => {
    if (!window.confirm('هل أنت متأكد من حذف جميع المخازن؟ هذا الإجراء لا يمكن التراجع عنه وقد يؤثر على المنتجات المرتبطة بالمخازن.')) {
      return;
    }

    try {
      setLoading(true);

      // الحصول على قائمة المخازن الحالية
      const currentWarehouses = await getAllWarehouses();
      console.log(`Found ${currentWarehouses.length} warehouses to delete`);

      if (currentWarehouses.length === 0) {
        setSnackbar({
          open: true,
          message: 'لا توجد مخازن لحذفها',
          severity: 'info',
        });
        setLoading(false);
        return;
      }

      // محاولة حذف المخازن واحداً تلو الآخر
      let successCount = 0;
      let errorCount = 0;

      for (const warehouse of currentWarehouses) {
        try {
          console.log(`Deleting warehouse: ${warehouse.id} (${warehouse.name})`);
          await deleteWarehouse(warehouse.id, true); // استخدام force=true لتجاوز التحققات
          successCount++;
        } catch (warehouseError) {
          console.error(`Error deleting warehouse ${warehouse.id}:`, warehouseError);
          errorCount++;
        }
      }

      // عرض رسالة مناسبة بناءً على نتيجة الحذف
      if (successCount === currentWarehouses.length) {
        setSnackbar({
          open: true,
          message: `تم حذف جميع المخازن بنجاح (${successCount} مخزن)`,
          severity: 'success',
        });
      } else if (successCount > 0 && errorCount > 0) {
        setSnackbar({
          open: true,
          message: `تم حذف ${successCount} مخزن بنجاح، وفشل حذف ${errorCount} مخزن`,
          severity: 'warning',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'فشل في حذف جميع المخازن',
          severity: 'error',
        });
      }

      // إعادة تحميل المخازن
      await loadWarehouses();
    } catch (error) {
      console.error('Error in handleDeleteAllWarehouses:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء محاولة حذف المخازن',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // إنشاء مخزن افتراضي
  const handleCreateDefaultWarehouse = async () => {
    try {
      setLoading(true);
      const warehouseId = await createDefaultWarehouse();
      setSnackbar({
        open: true,
        message: `تم إنشاء المخزن الافتراضي بنجاح (${warehouseId})`,
        severity: 'success',
      });
      await loadWarehouses();
    } catch (error) {
      console.error('Error creating default warehouse:', error);
      setSnackbar({
        open: true,
        message: 'فشل في إنشاء المخزن الافتراضي',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // إعادة تعيين المخازن (حذف الكل وإنشاء مخزن افتراضي)
  const handleResetWarehouses = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة تعيين المخازن؟ سيتم حذف جميع المخازن وإنشاء مخزن افتراضي جديد. هذا الإجراء لا يمكن التراجع عنه.')) {
      return;
    }

    try {
      setLoading(true);

      // محاولة حذف جميع المخازن أولاً
      try {
        await deleteAllWarehouses();
        setSnackbar({
          open: true,
          message: 'تم حذف جميع المخازن بنجاح',
          severity: 'success',
        });
      } catch (deleteError) {
        console.error('Error deleting all warehouses:', deleteError);
        setSnackbar({
          open: true,
          message: 'فشل في حذف جميع المخازن، سيتم محاولة إنشاء مخزن افتراضي',
          severity: 'warning',
        });
      }

      // محاولة إنشاء مخزن افتراضي جديد
      try {
        const warehouseId = await createDefaultWarehouse();
        setSnackbar({
          open: true,
          message: `تم إنشاء مخزن افتراضي جديد بنجاح (${warehouseId})`,
          severity: 'success',
        });
      } catch (createError) {
        console.error('Error creating default warehouse:', createError);
        setSnackbar({
          open: true,
          message: 'فشل في إنشاء المخزن الافتراضي',
          severity: 'error',
        });
      }

      // إعادة تحميل المخازن
      await loadWarehouses();
    } catch (error) {
      console.error('Error in handleResetWarehouses:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إعادة تعيين المخازن',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // إغلاق الإشعار
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          أدوات الإدارة
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          هذه الصفحة تحتوي على أدوات إدارية متقدمة. يرجى استخدامها بحذر.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* إصلاح تكلفة قطع الغيار */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            إصلاح البيانات
          </Typography>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                إصلاح تكلفة قطع الغيار
              </Typography>
              <Typography variant="body2" color="text.secondary">
                إصلاح مشكلة تكلفة قطع الغيار في التقارير وتوحيد استخدام الأسعار في النظام.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/admin/parts-cost-fix')}
              >
                فتح أداة الإصلاح
              </Button>
            </CardActions>
          </Card>
        </Box>

        {/* ترحيل دفعات ديون الصيانة إلى الخزينة */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            ترحيل دفعة دين (صيانة) إلى الخزينة
          </Typography>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                استخدم هذه الأداة لإضافة معاملة خزينة مفقودة لدفعة دين نقدية مسجلة سابقاً.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="رقم طلب الصيانة"
                  type="number"
                  value={backfillParams.orderNumber}
                  onChange={(e) => setBackfillParams(p => ({ ...p, orderNumber: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="اسم العميل"
                  value={backfillParams.customerName}
                  onChange={(e) => setBackfillParams(p => ({ ...p, customerName: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="تاريخ السداد"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={backfillParams.dateISO}
                  onChange={(e) => setBackfillParams(p => ({ ...p, dateISO: e.target.value }))}
                  fullWidth
                />
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                disabled={backfilling || !backfillParams.orderNumber || !backfillParams.customerName || !backfillParams.dateISO}
                onClick={async () => {
                  if (!window.confirm('هل تريد ترحيل الدفعة إلى الخزينة؟ سيتم إنشاء معاملة خزينة إذا كانت مفقودة.')) return;
                  try {
                    setBackfilling(true);
                    const res = await backfillMaintenanceDebtPaymentToTreasury({
                      orderNumber: backfillParams.orderNumber,
                      customerName: backfillParams.customerName,
                      dateISO: backfillParams.dateISO,
                    });
                    setSnackbar({ open: true, message: `تم: إنشاء ${res.created} / تخطي ${res.skipped}` as string, severity: 'success' });
                  } catch (err) {
                    console.error(err);
                    setSnackbar({ open: true, message: 'فشل في الترحيل. تفقد السجل.', severity: 'error' });
                  } finally {
                    setBackfilling(false);
                  }
                }}
              >
                {backfilling ? 'جارٍ التنفيذ...' : 'تنفيذ الترحيل'}
              </Button>
            </CardActions>
          </Card>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          إدارة المخازن
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  إعادة تعيين المخازن
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  حذف جميع المخازن وإنشاء مخزن افتراضي جديد. استخدم هذا الخيار لحل مشاكل المخازن.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  onClick={handleResetWarehouses}
                  disabled={loading}
                  fullWidth
                >
                  إعادة تعيين المخازن
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  حذف جميع المخازن
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  حذف جميع المخازن من النظام. هذا الإجراء لا يمكن التراجع عنه.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteAllWarehouses}
                  disabled={loading}
                  fullWidth
                >
                  حذف جميع المخازن
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  إنشاء مخزن افتراضي
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  إنشاء مخزن افتراضي جديد باسم "المخزن الرئيسي".
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateDefaultWarehouse}
                  disabled={loading}
                  fullWidth
                >
                  إنشاء مخزن افتراضي
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            المخازن الحالية ({warehouses.length})
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : warehouses.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              لا توجد مخازن حالياً
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {warehouses.map((warehouse) => (
                <Paper key={warehouse.id} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1">
                    {warehouse.name} {warehouse.isMain && '(المخزن الرئيسي)'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    المعرف: {warehouse.id}
                  </Typography>
                  {warehouse.location && (
                    <Typography variant="body2" color="text.secondary">
                      الموقع: {warehouse.location}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/warehouse')}
          >
            العودة إلى صفحة المخازن
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={loadWarehouses}
            disabled={loading}
          >
            تحديث القائمة
          </Button>
        </Box>
      </Paper>

      {/* الإشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminToolsPage;
